import type { Patient } from '@medplum/fhirtypes';

import {
  INDIVIDUAL_PRONOUNS_URL,
  US_CORE_ETHNICITY_URL,
  US_CORE_GENDER_IDENTITY_URL,
  US_CORE_RACE_URL,
} from '@eluve/fhir-renderer';

export type PatientStatus = 'active' | 'deceased' | 'left-practice';

export const PATIENT_STATUS_LABELS: Record<PatientStatus, string> = {
  active: 'Active',
  deceased: 'Deceased',
  'left-practice': 'Left practice',
};

export function patientStatus(patient: Patient): PatientStatus {
  if (isDeceased(patient)) return 'deceased';
  if (hasLeftPractice(patient)) return 'left-practice';
  return 'active';
}

export type PatientStatusDimension = 'deceased' | 'left-practice';

/** A requested transition on one status dimension; `set: false` reverses it. */
export type PatientStatusAction = {
  dimension: PatientStatusDimension;
  set: boolean;
};

export function isDeceased(patient: Patient): boolean {
  return Boolean(patient.deceasedBoolean || patient.deceasedDateTime);
}

export function hasLeftPractice(patient: Patient): boolean {
  return patient.active === false;
}

export function statusActionFor(
  patient: Patient,
  dimension: PatientStatusDimension,
): PatientStatusAction {
  const flagged =
    dimension === 'deceased' ? isDeceased(patient) : hasLeftPractice(patient);
  return { dimension, set: !flagged };
}

const STATUS_MENU_LABELS: Record<
  PatientStatusDimension,
  Record<'set' | 'clear', string>
> = {
  deceased: {
    set: 'Mark as deceased…',
    clear: 'Remove deceased status…',
  },
  'left-practice': {
    set: 'Mark as left the practice…',
    clear: 'Mark as active…',
  },
};

export function statusMenuLabel(
  patient: Patient,
  dimension: PatientStatusDimension,
): string {
  const action = statusActionFor(patient, dimension);
  return STATUS_MENU_LABELS[dimension][action.set ? 'set' : 'clear'];
}

const PHONE_USE_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  home: 'Home',
  work: 'Work',
};

export function phoneLabel(use: string | undefined): string {
  return (use && PHONE_USE_LABELS[use]) || 'Phone';
}

function hasExtension(patient: Patient, url: string): boolean {
  return Boolean(patient.extension?.some((extension) => extension.url === url));
}

export function hasIdentityDemographics(patient: Patient): boolean {
  return Boolean(
    patient.gender ||
      patient.maritalStatus ||
      patient.communication?.length ||
      hasExtension(patient, US_CORE_GENDER_IDENTITY_URL) ||
      hasExtension(patient, INDIVIDUAL_PRONOUNS_URL) ||
      hasExtension(patient, US_CORE_RACE_URL) ||
      hasExtension(patient, US_CORE_ETHNICITY_URL),
  );
}

export function hasContactDetails(patient: Patient): boolean {
  return Boolean(
    patient.telecom?.length ||
      patient.address?.length ||
      patient.contact?.length,
  );
}

/** Local-timezone date for appointment timestamps (visits are local events). */
export function formatVisitDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
